/**
 * Workflow v2 StepShell — step-agnostic body wrapper.
 *
 * Provides the scrollable content region + the locked-state empty
 * card. The visible "Step N: Title • Status" header was dropped —
 * each step body owns its own top-of-content chrome (mode switcher
 * for Step 0, tab nav for Step 1, table toolbar for future steps).
 *
 * Locked-step behavior: if step N≥1 isn't unlocked yet, renders a
 * single empty-state card with a "Go to Step N-1" CTA instead of
 * the body.
 *
 * Story H9 — migrated `.jsx` → `.tsx`. Closes the type-strictness
 * gap flagged in the original hardening audit (risk #13).
 */
import React from 'react';
import { Button } from '../../../components/ui/Button2';
import { findStep, STEP_CATALOG } from '../state/workflowV2StepCatalog';
import { isStepLocked } from '../state/workflowV2Reducer';
import { Icon } from '../../../components/ui/Icon';
import type { WorkflowActions, WorkflowV2State } from '../state/workflowV2Types';
import styles from './StepShell.module.css';

// Button2 is a JSX forwardRef primitive; cast for use from TSX (mirrors the
// DataTableAny/RowActionsMenuAny convention across the step-components).
const ButtonAny = Button as unknown as React.ComponentType<any>;

export interface StepShellProps {
  state: WorkflowV2State;
  actions: WorkflowActions;
  children?: React.ReactNode;
}

export default function StepShell({ state, actions, children }: StepShellProps) {
  const stepId = state.selectedStepId;
  const stepInfo = findStep(stepId);
  const locked = isStepLocked(state, stepId);

  if (locked) {
    const prevTitle = STEP_CATALOG.find((s) => s.id === stepId - 1)?.title;
    return (
      <div className={styles.shellPad}>
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}><Icon name="lock" size={20} /></div>
          <div className={styles.emptyStateTitle}>
            Step {stepId} — {stepInfo?.title} is locked
          </div>
          <div className={styles.emptyStateDescription}>
            Approve Step {stepId - 1} ({prevTitle}) before this step becomes available. Outputs from
            earlier steps are used as structured input for this stage.
          </div>
          <div className={styles.emptyStateAction}>
            <ButtonAny
              variant="outline"
              size="sm"
              onClick={() => actions.selectStep(stepId - 1)}
            >
              Go to Step {stepId - 1}
            </ButtonAny>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.shell}>
      <div className={styles.contentScroll}>
        <div className={styles.content}>{children}</div>
      </div>
    </div>
  );
}
